import { useMemo } from "react";
import { motion } from "motion/react";

import { usePlan } from "@/features/auth/hooks/usePlan";
import {
  CategoryGrid,
  SHOP_PAGE_CONFIG,
  SHOP_CATEGORIES_CARDS,
} from "@/features/shop";
import { CmsAutoScrollSlider } from "@/features/shop/cms/components/CmsAutoScrollSlider";
import { CmsZoneLayout } from "@/features/shop/cms/components/CmsZoneLayout";
import { CmsPreviewBanner } from "@/features/shop/cms/preview/CmsPreviewBanner";
import type { CmsZone } from "@/features/shop/cms/types/cms.types";
import { SearchBarAutocompleteSection } from "@/features/shop/components/SearchBarAutocomplete";
import {
  STAGGER_DELAY,
  INITIAL_ANIMATION,
  ANIMATE_IN,
  SECTION_TRANSITION,
} from "@/features/shop/config/animations";
import { useShopPageLoadingState } from "@/features/shop/hooks/useShopPageLoadingState";
import { ShopLoadingState } from "@/features/shop/shared/components/ShopLoadingState";

export const ShopPage = () => {
  const plan = usePlan();
  const { isLoading, cmsPageDetail } = useShopPageLoadingState();

  const filteredCategories = useMemo(() => {
    if (!plan?.features) return SHOP_CATEGORIES_CARDS;

    return SHOP_CATEGORIES_CARDS.filter((category) => {
      if (!category.featureFlag) return true;
      return plan.features[category.featureFlag];
    });
  }, [plan?.features]);

  if (isLoading || !cmsPageDetail) {
    return <ShopLoadingState />;
  }

  const { code: pageCode, zones, preview } = cmsPageDetail;
  const zonesByCode = zones.reduce<Record<string, CmsZone>>((acc, zone) => {
    acc[zone.code.toLowerCase()] = zone;
    return acc;
  }, {});
  const getZone = (code: string) => zonesByCode[code.toLowerCase()];

  const hasSearchZone = zones.some((zone) => zone.code.toLowerCase().startsWith("search"));

  const topKinds: Array<"slider" | "promotion" | "infinite" | "search"> = ["slider"];
  if (hasSearchZone) {
    topKinds.push("search");
  }
  const hasTopCmsZones = zones.some((zone) => {
    const code = zone.code.toLowerCase();
    return (zone.carrousel && code.startsWith("slider"))
      || (hasSearchZone && code.startsWith("search"));
  });

  const hasBottomCmsZones = zones.some((zone) => {
    const code = zone.code.toLowerCase();
    return (!zone.carrousel && code.startsWith("promotional-card"))
      || code.startsWith("infinit");
  });

  const infiniteSliderZone1 = getZone("infinite_slider_1");
  const infiniteSliderZone2 = getZone("infinite_slider_2");

  return (
    <div className="flex flex-col gap-6 page-animation">
      {preview && <CmsPreviewBanner pageCode={pageCode} draftUpdatedAt={preview.draft_updated_at} />}

      {hasTopCmsZones && (
        <motion.div
          initial={INITIAL_ANIMATION}
          animate={ANIMATE_IN}
          transition={{ ...SECTION_TRANSITION, delay: 0 * STAGGER_DELAY }}
        >
          <CmsZoneLayout
            pageCode={pageCode}
            zones={zones}
            autoplayInterval={SHOP_PAGE_CONFIG.BANNER_SLIDER_AUTOPLAY_INTERVAL}
            kinds={topKinds}
          />
        </motion.div>
      )}

      {!hasSearchZone && (
        <motion.div
          initial={INITIAL_ANIMATION}
          animate={ANIMATE_IN}
          transition={{ ...SECTION_TRANSITION, delay: 1 * STAGGER_DELAY }}
          className="relative z-30 overflow-visible"
        >
          <SearchBarAutocompleteSection />
        </motion.div>
      )}

      {filteredCategories.length > 0 && (
        <motion.div
          initial={INITIAL_ANIMATION}
          animate={ANIMATE_IN}
          transition={{ ...SECTION_TRANSITION, delay: 2 * STAGGER_DELAY }}
        >
          <CategoryGrid categories={filteredCategories} />
        </motion.div>
      )}

      {hasBottomCmsZones && (
        <motion.div
          initial={INITIAL_ANIMATION}
          animate={ANIMATE_IN}
          transition={{ ...SECTION_TRANSITION, delay: 3 * STAGGER_DELAY }}
        >
          <CmsZoneLayout
            pageCode={pageCode}
            zones={zones}
            autoplayInterval={SHOP_PAGE_CONFIG.BANNER_SLIDER_AUTOPLAY_INTERVAL}
            kinds={["promotion", "infinite"]}
          />
        </motion.div>
      )}

      {infiniteSliderZone1 && (
        <motion.div
          initial={INITIAL_ANIMATION}
          animate={ANIMATE_IN}
          transition={{ ...SECTION_TRANSITION, delay: 4 * STAGGER_DELAY }}
          className="relative z-10 overflow-hidden"
        >
          <CmsAutoScrollSlider
            pageCode={pageCode}
            zoneCode={infiniteSliderZone1.code}
            scrollSpeed={SHOP_PAGE_CONFIG.AUTO_SCROLL_SLIDER_SPEED}
          />
        </motion.div>
      )}

      {infiniteSliderZone2 && (
        <motion.div
          initial={INITIAL_ANIMATION}
          animate={ANIMATE_IN}
          transition={{ ...SECTION_TRANSITION, delay: 5 * STAGGER_DELAY }}
          className="relative z-10 overflow-hidden"
        >
          <CmsAutoScrollSlider
            pageCode={pageCode}
            zoneCode={infiniteSliderZone2.code}
            scrollSpeed={SHOP_PAGE_CONFIG.ALT_AUTO_SCROLL_SLIDER_SPEED}
            offsetX={-80}
          />
        </motion.div>
      )}
    </div>
  );
};
